import { useEffect, useState } from "react";

/**
 * Extract the dominant color of an image client-side via a tiny canvas.
 * Skips near-black and near-white pixels so we don't bias toward sky/shadow.
 * Returns either an "rgb(r,g,b)" string, or null while loading / on failure
 * (e.g. CORS-tainted canvas).
 *
 * @param {string} imageUrl  absolute or resolved image URL
 * @param {number} [sample=32] downscaled width to sample (height auto)
 */
export function useDominantColor(imageUrl, sample = 32) {
  const [color, setColor] = useState(null);
  useEffect(() => {
    if (!imageUrl) {
      setColor(null);
      return;
    }
    let cancelled = false;
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const w = sample;
        const h = Math.max(1, Math.round((img.naturalHeight / img.naturalWidth) * w));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        const data = ctx.getImageData(0, 0, w, h).data;
        let r = 0, g = 0, b = 0, count = 0;
        for (let i = 0; i < data.length; i += 4) {
          const rr = data[i], gg = data[i + 1], bb = data[i + 2];
          const lum = (rr + gg + bb) / 3;
          if (lum < 18 || lum > 238) continue;
          r += rr; g += gg; b += bb; count += 1;
        }
        if (!count || cancelled) return;
        r = Math.round(r / count);
        g = Math.round(g / count);
        b = Math.round(b / count);
        setColor(`rgb(${r},${g},${b})`);
      } catch {
        /* canvas tainted — caller handles null */
      }
    };
    img.src = imageUrl;
    return () => { cancelled = true; };
  }, [imageUrl, sample]);
  return color;
}

/**
 * Desaturate an rgb(...) color by mixing each channel toward its luminance.
 * mix=0 returns the original color; mix=1 returns pure grayscale.
 * Returns the input unchanged if it doesn't match the rgb() pattern.
 */
export function desaturate(rgbString, mix = 0.6) {
  if (!rgbString) return rgbString;
  const m = rgbString.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
  if (!m) return rgbString;
  const r = +m[1], g = +m[2], b = +m[3];
  const lum = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
  const blend = (c) => Math.round(c * (1 - mix) + lum * mix);
  return `rgb(${blend(r)},${blend(g)},${blend(b)})`;
}
